import React from "react";
import { cn } from "../../lib/utils";
import { useResolvedTheme } from "../../design/theme";

/*
 * Real illustrated campus scenery behind the marketing heroes (2026-08-21 product decision).
 *
 * The Intercom-helpdesk discipline, executed with committed artwork: painterly scenery that
 * RECEDES — a per-page veil gradient washes the copy zone so headlines and product screenshots
 * stay the only sharp, high-contrast elements, and a mask fade melts the band into the page.
 * Since 2026-08-22 the art re-lights with the theme instead of hiding: each scene ships a NIGHT
 * painting served under [data-theme=dark] (useResolvedTheme keeps the choice live, and only the
 * active variant's <picture> mounts, so a theme flip never double-fetches). High-contrast keeps
 * the art hidden entirely (index.css) — its canvas is pure-contrast by contract.
 *
 * Delivery rungs come from scripts/generate_hero_campus_art_assets.py (AVIF+WebP, manifest-
 * verified via `npm run verify:hero-campus`); the 2 MB PNG sources never ship. Decorative by
 * construction: aria-hidden, empty alt, pointer-events-none, fetchpriority=low so the product
 * story image keeps bandwidth priority for LCP.
 */

const ARTWORKS = {
  landing: {
    base: "/marketing/hero-campus/landing-campus-constellation",
    widths: [640, 960, 1280, 1600, 1821],
    width: 1821,
    height: 864,
    dark: {
      base: "/marketing/hero-campus/landing-campus-constellation-dark",
      widths: [640, 960, 1280, 1600, 1821],
      width: 1821,
      height: 864,
    },
  },
  modules: {
    base: "/marketing/hero-campus/modules-knowledge-canopy",
    widths: [640, 960, 1280, 1600, 1821],
    width: 1821,
    height: 864,
    dark: {
      base: "/marketing/hero-campus/modules-knowledge-canopy-dark",
      widths: [640, 960, 1280, 1600, 1821],
      width: 1821,
      height: 864,
    },
  },
  aira: {
    base: "/marketing/hero-campus/aira-girl-assistant",
    widths: [640, 960, 1280, 1600, 1816],
    width: 1816,
    height: 866,
    dark: {
      base: "/marketing/hero-campus/aira-girl-assistant-dark",
      widths: [640, 960, 1280, 1600, 1817],
      width: 1817,
      height: 866,
    },
  },
};

const srcSetFor = (art, format) => art.widths.map((w) => `${art.base}.w${w}.${format} ${w}w`).join(", ");

function ArtPicture({ art, sizes, imgClassName }) {
  return (
    <picture className="contents">
      <source type="image/avif" srcSet={srcSetFor(art, "avif")} sizes={sizes} />
      <img
        src={`${art.base}.w1280.webp`}
        srcSet={srcSetFor(art, "webp")}
        sizes={sizes}
        alt=""
        width={art.width}
        height={art.height}
        loading="eager"
        fetchpriority="low"
        decoding="async"
        draggable="false"
        className={imgClassName}
      />
    </picture>
  );
}

export default function HeroCampusArt({ artwork, className, veilClassName, sizes = "100vw", stage = false }) {
  const theme = useResolvedTheme();
  const entry = ARTWORKS[artwork];
  const art = entry && theme === "dark" && entry.dark ? entry.dark : entry;
  if (!art) return null;
  if (stage) {
    // Scenic scroll stage: a sticky full-viewport backdrop under a .hero-campus-stage-wrap of
    // 3-4 sections. The FULL painting grounds at the viewport bottom (natural aspect, no crop);
    // the scene-sky gradient fills above it; the veil keeps overlaying copy sovereign.
    return (
      <div
        className={cn("hero-campus-art hero-campus-stage", className)}
        aria-hidden="true"
        data-testid={`hero-campus-art-${artwork}`}
      >
        <div className="hero-campus-stage-viewport">
          <ArtPicture art={art} sizes={sizes} />
          {veilClassName ? <div className={cn("absolute inset-0", veilClassName)} /> : null}
        </div>
      </div>
    );
  }
  return (
    <div
      className={cn("hero-campus-art pointer-events-none absolute select-none overflow-hidden", className)}
      aria-hidden="true"
      data-testid={`hero-campus-art-${artwork}`}
    >
      <ArtPicture art={art} sizes={sizes} imgClassName="h-full w-full object-cover" />
      {veilClassName ? <div className={cn("absolute inset-0", veilClassName)} /> : null}
    </div>
  );
}
